import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  Target, 
  Award, 
  Lightbulb, 
  ArrowRight, 
  CheckCircle
} from "lucide-react";
import { 
  Container, 
  Section, 
  Grid, 
  Card, 
  PrimaryButton, 
  SecondaryButton, 
  Badge,
  FlexContainer 
} from "../styles/GlobalStyles";

// Enhanced About Us Section
const AboutSection = styled(Section)`
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--primary-50) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-12);
  }
  
  @media (max-width: 480px) {
    margin-bottom: var(--space-8);
  }
  
  .badge {
    margin-bottom: var(--space-4);
    
    @media (max-width: 480px) {
      margin-bottom: var(--space-3);
    }
  }
  
  h2 {
    font-size: var(--text-5xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 1024px) {
      font-size: var(--text-4xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
      margin-bottom: var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-2xl);
      margin-bottom: var(--space-3);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 600px;
    margin: 0 auto;
    line-height: var(--leading-relaxed);
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      max-width: 100%;
      padding: 0 var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-base);
      padding: 0 var(--space-2);
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: var(--space-16);
  align-items: center;
  margin-bottom: var(--space-20);
  
  @media (max-width: 1024px) {
    gap: var(--space-12);
    margin-bottom: var(--space-16);
  }
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--space-8);
    text-align: center;
    margin-bottom: var(--space-12);
  }
  
  @media (max-width: 480px) {
    gap: var(--space-6);
    margin-bottom: var(--space-8);
  }
`;

const ContentText = styled.div`
  h3 {
    font-size: var(--text-3xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 700;
    
    @media (max-width: 1024px) {
      font-size: var(--text-2xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-xl);
      margin-bottom: var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-lg);
      margin-bottom: var(--space-3);
    }
  }
  
  p {
    font-size: var(--text-lg);
    color: var(--gray-600);
    line-height: var(--leading-relaxed);
    margin-bottom: var(--space-6);
    
    @media (max-width: 768px) {
      font-size: var(--text-base);
      margin-bottom: var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-sm);
      margin-bottom: var(--space-3);
    }
  }
  
  .highlight {
    color: var(--primary-600);
    font-weight: 600;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-8);
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  
  .icon {
    width: 24px;
    height: 24px;
    color: var(--secondary-600);
    flex-shrink: 0;
  }
  
  .text {
    font-size: var(--text-base);
    color: var(--gray-700);
    font-weight: 500;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  
  .main-image {
    width: 100%;
    border-radius: var(--radius-3xl);
    box-shadow: var(--shadow-2xl);
    transition: all var(--transition-slow);
    transform: perspective(1000px) rotateY(-8deg) rotateX(5deg);
    
    &:hover {
      transform: perspective(1000px) rotateY(-4deg) rotateX(2deg) scale(1.02);
      box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
    }
  }
  
  .floating-card {
    position: absolute;
    bottom: -20px;
    right: -20px;
    background: var(--gray-0);
    padding: var(--space-6);
    border-radius: var(--radius-2xl);
    box-shadow: var(--shadow-xl);
    border: 1px solid var(--gray-200);
    max-width: 200px;
    
    @media (max-width: 968px) {
      position: static;
      margin-top: var(--space-6);
      max-width: none;
    }
    
    .stat-number {
      font-size: var(--text-3xl);
      font-weight: 800;
      color: var(--primary-600);
      display: block;
      line-height: 1;
    }
    
    .stat-label {
      font-size: var(--text-sm);
      color: var(--gray-600);
      font-weight: 500;
    }
  }
`;

const ValuesGrid = styled(Grid)`
  margin-top: var(--space-20);
  
  &.cols-3 {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`;

const ValueCard = styled(Card)`
  text-align: center;
  padding: var(--space-10);
  border: 2px solid transparent;
  transition: all var(--transition-normal);
  
  &:hover {
    border-color: var(--primary-200);
    transform: translateY(-8px) scale(1.02);
  }
  
  .icon {
    width: 64px;
    height: 64px;
    margin: 0 auto var(--space-6);
    background: var(--gradient-primary);
    border-radius: var(--radius-2xl);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-0);
  }
  
  h4 {
    font-size: var(--text-xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    font-weight: 700;
  }
  
  p {
    color: var(--gray-600);
    line-height: var(--leading-relaxed);
    margin: 0;
  }
`;

const StatsSection = styled.div`
  background: var(--gradient-primary);
  padding: var(--space-20) 0;
  color: var(--gray-0);
  text-align: center;
  margin-top: var(--space-20);
  border-radius: var(--radius-3xl);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><defs><radialGradient id="a" cx="50%" cy="50%"><stop offset="0%" stop-color="white" stop-opacity="0.1"/><stop offset="100%" stop-color="white" stop-opacity="0"/></radialGradient></defs><rect width="1000" height="1000" fill="url(%23a)"/></svg>');
    pointer-events: none;
  }
  
  h3 {
    font-size: var(--text-3xl);
    margin-bottom: var(--space-12);
    color: var(--gray-0);
    font-weight: 700;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-8);
  position: relative;
  z-index: 2;
`;

const StatCard = styled.div`
  .number {
    font-size: var(--text-5xl);
    font-weight: 900;
    color: var(--gray-0);
    display: block;
    line-height: 1;
    margin-bottom: var(--space-2);
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
  }
  
  .label {
    font-size: var(--text-base);
    opacity: 0.9;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: var(--space-20);
  
  h3 {
    font-size: var(--text-3xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 700;
  }
  
  p {
    font-size: var(--text-lg);
    color: var(--gray-600);
    margin-bottom: var(--space-8);
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const features = [
  "Fresh perspective and modern approach",
  "Live product: WillovateResto.com IMS",
  "Custom software development",
  "Responsive and dedicated team",
  "Growing with your business needs"
];

const values = [
  {
    icon: <Lightbulb size={32} />,
    title: "Innovation",
    description: "We bring fresh ideas and modern solutions to solve real business challenges with creativity."
  },
  {
    icon: <Target size={32} />,
    title: "Commitment",
    description: "As a startup, we're committed to delivering quality work and building long-term relationships with our clients."
  },
  {
    icon: <Users size={32} />,
    title: "Growth",
    description: "We believe in growing together - as our clients succeed, we learn and evolve to serve them better."
  }
];

const stats = [
  { number: "2025", label: "Year Founded" },
  { number: "1+", label: "Live Product" },
  { number: "100%", label: "Dedication" },
  { number: "∞", label: "Potential" }
];

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <AboutSection>
      <Container>
        <SectionHeader>
          <Badge className="badge primary">
            <Award size={12} />
            About Willovate
          </Badge>
          <h2>Software Development Company for Every Industry</h2>
          <p className="subtitle">
            We're a software development company that builds custom applications for businesses 
            across multiple industries. From restaurant management to web applications, 
            we turn your ideas into powerful software solutions.
          </p>
        </SectionHeader>

        <ContentGrid>
          <ContentText>
            <h3>
              From <span className="highlight">Concept</span> to Live Applications
            </h3>
            <p>
              Willovate specializes in custom software development for various industries. 
              As a growing startup, we're building innovative solutions and expanding our 
              expertise across Full-Stack Development, AI/ML, and IoT technologies.
            </p>
            <p>
              Whether you need a web application, database solution, or custom software, 
              we work with businesses of all sizes to create applications that drive 
              growth and efficiency.
            </p>
            
            <FeaturesList>
              {features.map((feature, index) => (
                <FeatureItem key={index}>
                  <CheckCircle className="icon" />
                  <span className="text">{feature}</span>
                </FeatureItem>
              ))}
            </FeaturesList>

            <FlexContainer style={{ gap: 'var(--space-4)' }}>
              <PrimaryButton onClick={() => navigate('/service')}>
                Learn More
                <ArrowRight size={16} />
              </PrimaryButton>
              <SecondaryButton onClick={() => navigate('/service')}>
                Our Services
              </SecondaryButton>
            </FlexContainer>
          </ContentText>

          <ImageContainer>
            <img 
              src={require("../assets/heroBg.jpeg")} 
              alt="Our team at work"
              className="main-image"
            />
            <div className="floating-card">
              <span className="stat-number">2025</span>
              <span className="stat-label">Founded</span>
            </div>
          </ImageContainer>
        </ContentGrid>

        <ValuesGrid className="cols-3">
          {values.map((value, index) => (
            <ValueCard key={index}>
              <div className="icon">
                {value.icon}
              </div>
              <h4>{value.title}</h4>
              <p>{value.description}</p>
            </ValueCard>
          ))}
        </ValuesGrid>

        <StatsSection>
          <Container>
            <h3>Our Startup Journey</h3>
            <StatsGrid>
              {stats.map((stat, index) => (
                <StatCard key={index}>
                  <span className="number">{stat.number}</span>
                  <span className="label">{stat.label}</span>
                </StatCard>
              ))}
            </StatsGrid>
          </Container>
        </StatsSection>

        <CTASection>
          <h3>Ready to Start Your Journey?</h3>
          <p>
            Let's work together to bring your vision to life with cutting-edge 
            technology and innovative solutions.
          </p>
          <FlexContainer className="center" style={{ gap: 'var(--space-4)' }}>
            <PrimaryButton onClick={() => navigate('/contact')}>
              Get Started Today
              <ArrowRight size={16} />
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/contact')}>
              Schedule a Call
            </SecondaryButton>
          </FlexContainer>
        </CTASection>
      </Container>
    </AboutSection>
  );
};

export default AboutUs;
